"use client";

import {
  createTheme,
  Input,
  Button,
  ActionIcon,
  Badge,
  MultiSelect,
  Popover,
  Select,
} from "@mantine/core";
import { Inter } from "next/font/google";

import classes from "./app/lib/styles/MantineTheme.module.css";

// Single typeface across product and marketing: Inter for headings, body, and UI.
const inter = Inter({
  subsets: ["latin"],
  display: "swap",
});

// Shared overlay styling (blurred, slightly blue) for modal-like surfaces.
// Exported so the composable Drawer.Overlay can match the themed default, which
// Mantine only applies to the monolithic <Drawer> via overlayProps.
export const overlayProps = {
  blur: 3,
  color: "rgba(1, 19, 36, 0.6)",
};

export const theme = createTheme({
  black: "var(--mantine-color-dark-7)",
  components: {
    ActionIcon: ActionIcon.extend({
      classNames: classes,
      defaultProps: {
        loaderProps: {
          type: "dots",
        },
      },
    }),
    Badge: Badge.extend({
      classNames: classes,
    }),
    Button: Button.extend({
      classNames: classes,
      defaultProps: {
        loaderProps: {
          type: "dots",
        },
      },
    }),
    Drawer: {
      defaultProps: {
        overlayProps,
      },
    },
    LoadingOverlay: {
      defaultProps: {
        // Loading overlays are always scoped to an in-content container, so
        // they must sit below AppShell chrome (100) and modals (200): the
        // Mantine default of 400 paints over the hover-expanded navbar and
        // open modals. 50 still clears in-content stacked elements (graph
        // nodes, sticky headers), which stay in the single digits.
        zIndex: 50,
        overlayProps: {
          blur: 3,
          color: "rgba(1, 19, 36, 0.6)",
        },
      },
    },
    Input: Input.extend({ classNames: classes }),
    InputWrapper: {
      styles: {
        description: {
          marginTop: "8px",
        },
        label: {
          marginBottom: "6px",
        },
        error: {
          marginTop: "8px",
        },
      },
      defaultProps: {
        inputWrapperOrder: ["label", "input", "description", "error"],
      },
    },
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        checkIconPosition: "right",
      },
    }),
    Modal: {
      defaultProps: {
        overlayProps: {
          blur: 3,
          // A cooler, more blue overlay
          color: "rgba(1, 19, 36, 0.6)",
        },
      },
    },
    Select: Select.extend({
      defaultProps: {
        checkIconPosition: "right",
        // Show a single right-section icon: the clear (×) button when a value is
        // selected, otherwise the dropdown chevron. Avoids the cramped ×-plus-chevron
        // pair colliding with long selected values. Note: "clear" mode ignores a
        // custom `rightSection` — a Select that needs one must set clearSectionMode="both".
        clearSectionMode: "clear",
      },
    }),
    Popover: Popover.extend({
      defaultProps: {
        overlayProps: {
          blur: 3,
          color: "rgba(1, 19, 36, 0.6)",
        },
      },
    }),
  },
  fontFamily: inter.style.fontFamily,
  // Extend Mantine's default font sizes. xs/sm/md/lg/xl keep Mantine defaults.
  fontSizes: {
    "2xl": "1.5rem", // 24px — +4px above xl (20px), follows spacing gestalt of the scale
  },
  // Extend Mantine's default spacing with additional tokens.
  // xs/sm/md/lg/xl keep Mantine defaults; only custom tokens added.
  spacing: {
    "2xs": "2px",
    "2xl": "48px",
    "3xl": "64px",
  },
});
